import React, { useEffect, useState, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Collapse,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Divider,
  Paper,
  Chip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  ExpandMore,
  ChevronRight,
  FileCopyOutlined,
  Visibility,
  VisibilityOff,
} from "@material-ui/icons";

import api from "../../services/api";
import toastUtils from "../../utils/toastUtils";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  resourceRow: {
    display: "flex",
    alignItems: "center",
    padding: "4px 0",
    borderRadius: 4,
    "&:hover": { background: "#f5f5f5" },
  },
  fieldsBox: {
    paddingLeft: 38,
    paddingTop: 4,
    paddingBottom: 8,
    display: "flex",
    flexWrap: "wrap",
    gap: 4,
  },
  fieldLabel: {
    fontSize: 13,
    "& .MuiFormControlLabel-label": { fontSize: 13 },
  },
  urlBox: {
    background: "#1f2937",
    color: "#10b981",
    padding: theme.spacing(1.5),
    borderRadius: 6,
    fontFamily: "monospace",
    fontSize: 12,
    wordBreak: "break-all",
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  helper: {
    fontSize: 12,
    color: "#666",
    marginBottom: 8,
  },
  resourceLabel: {
    fontWeight: 600,
    fontSize: 14,
  },
  fieldChip: {
    fontFamily: "monospace",
    fontSize: 11,
  },
}));

const RESOURCE_LABELS = {
  stats: "📊 Estatísticas",
  users: "👤 Usuários (atendentes)",
  queues: "📋 Filas",
  whatsapps: "📱 Conexões WhatsApp",
  contacts: "👥 Contatos",
  tickets: "🎫 Tickets",
  tags: "🏷️ Tags",
};

const ApiRestModal = ({ open, onClose, integration, apiBaseUrl }) => {
  const classes = useStyles();

  const [name, setName] = useState("");
  const [allowedResources, setAllowedResources] = useState([]);
  const [allowedFields, setAllowedFields] = useState({});
  const [expanded, setExpanded] = useState({});
  const [schema, setSchema] = useState({ resources: [], fields: {} });
  const [tab, setTab] = useState(0);
  const [showToken, setShowToken] = useState(false);
  const [saving, setSaving] = useState(false);

  const isEdit = !!integration;

  // Buscar schema (recursos e campos disponíveis)
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/api-rest-integrations/schema");
        setSchema(data);
      } catch (err) {
        toastError(err);
      }
    })();
  }, [open]);

  // Inicializar campos do form quando abre
  useEffect(() => {
    if (!open) return;
    if (integration) {
      setName(integration.name || "");
      setAllowedResources(integration.allowedResources || []);
      setAllowedFields(integration.allowedFields || {});
      // expande tudo que está marcado
      const exp = {};
      (integration.allowedResources || []).forEach((r) => {
        exp[r] = true;
      });
      setExpanded(exp);
    } else {
      setName("");
      setAllowedResources([]);
      setAllowedFields({});
      setExpanded({});
    }
    setTab(0);
    setShowToken(false);
  }, [open, integration]);

  const toggleResource = (resource) => {
    setAllowedResources((prev) => {
      if (prev.includes(resource)) {
        // desmarcando: remove do allowedFields também
        setAllowedFields((af) => {
          const next = { ...af };
          delete next[resource];
          return next;
        });
        return prev.filter((r) => r !== resource);
      }
      // marcando: expande automaticamente
      setExpanded((e) => ({ ...e, [resource]: true }));
      return [...prev, resource];
    });
  };

  const toggleField = (resource, field) => {
    setAllowedFields((prev) => {
      const current = prev[resource] || [];
      if (current.includes(field)) {
        return { ...prev, [resource]: current.filter((f) => f !== field) };
      }
      return { ...prev, [resource]: [...current, field] };
    });
  };

  const selectAllFields = (resource, allFields) => {
    setAllowedFields((prev) => ({ ...prev, [resource]: [...allFields] }));
  };

  const clearFields = (resource) => {
    setAllowedFields((prev) => ({ ...prev, [resource]: [] }));
  };

  const isFieldChecked = (resource, field) => {
    const arr = allowedFields[resource];
    // Se array vazio ou não definido => todos liberados (default)
    if (!arr || arr.length === 0) return true;
    return arr.includes(field);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toastUtils.error("Informe o nome da integração", "Validação");
      return;
    }
    if (allowedResources.length === 0) {
      toastUtils.error("Selecione pelo menos um recurso", "Validação");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        allowedResources,
        // Não envia campos vazios (significa "todos") — só quando tem filtro
        allowedFields: Object.fromEntries(
          Object.entries(allowedFields).filter(
            ([r, arr]) =>
              allowedResources.includes(r) && Array.isArray(arr) && arr.length > 0
          )
        ),
      };
      let saved;
      if (isEdit) {
        const { data } = await api.put(
          `/api-rest-integrations/${integration.id}`,
          payload
        );
        saved = data;
      } else {
        const { data } = await api.post("/api-rest-integrations", payload);
        saved = data;
      }
      toastUtils.success(
        isEdit ? "Integração atualizada" : "Integração criada",
        "Salvo"
      );
      onClose(saved);
    } catch (err) {
      toastError(err);
    }
    setSaving(false);
  };

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toastUtils.success(`${label} copiado`, "Copiado");
  };

  const exampleUrl = useMemo(() => {
    if (!integration) return null;
    const firstResource = allowedResources[0] || "stats";
    return `${apiBaseUrl}/api/v1/external/${integration.token}/${firstResource}`;
  }, [integration, allowedResources, apiBaseUrl]);

  const allUrls = useMemo(() => {
    if (!integration) return [];
    return allowedResources.map((r) => ({
      resource: r,
      url: `${apiBaseUrl}/api/v1/external/${integration.token}/${r}`,
    }));
  }, [integration, allowedResources, apiBaseUrl]);

  const renderResourceTree = () => (
    <Box>
      <Typography className={classes.helper}>
        Marque os recursos e os campos que essa integração poderá ler.
        Se você marcar o recurso mas não marcar campos específicos, todos os
        campos do recurso ficam liberados.
      </Typography>
      {schema.resources.map((r) => {
        const fields = schema.fields[r] || [];
        const checked = allowedResources.includes(r);
        const open = !!expanded[r];
        const fieldsArr = allowedFields[r];
        const partial =
          checked &&
          Array.isArray(fieldsArr) &&
          fieldsArr.length > 0 &&
          fieldsArr.length < fields.length;

        return (
          <Box key={r}>
            <Box className={classes.resourceRow}>
              <IconButton
                size="small"
                onClick={() => setExpanded((e) => ({ ...e, [r]: !open }))}
              >
                {open ? <ExpandMore /> : <ChevronRight />}
              </IconButton>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={checked}
                    indeterminate={partial}
                    onChange={() => toggleResource(r)}
                    color="primary"
                  />
                }
                label={
                  <span className={classes.resourceLabel}>
                    {RESOURCE_LABELS[r] || r}{" "}
                    <Typography component="span" variant="caption" color="textSecondary">
                      ({fields.length} campos)
                    </Typography>
                  </span>
                }
              />
            </Box>
            <Collapse in={open && checked} unmountOnExit>
              <Box className={classes.fieldsBox}>
                <Box style={{ width: "100%", marginBottom: 4 }}>
                  <Button
                    size="small"
                    onClick={() => selectAllFields(r, fields)}
                    style={{ fontSize: 11, marginRight: 8 }}
                  >
                    Marcar todos
                  </Button>
                  <Button
                    size="small"
                    onClick={() => clearFields(r)}
                    style={{ fontSize: 11 }}
                  >
                    Desmarcar todos (=todos liberados)
                  </Button>
                </Box>
                {fields.map((f) => (
                  <FormControlLabel
                    key={f}
                    className={classes.fieldLabel}
                    control={
                      <Checkbox
                        size="small"
                        checked={isFieldChecked(r, f)}
                        onChange={() => toggleField(r, f)}
                        color="primary"
                      />
                    }
                    label={f}
                  />
                ))}
              </Box>
            </Collapse>
          </Box>
        );
      })}
    </Box>
  );

  const renderUrlsTab = () => {
    if (!integration) {
      return (
        <Box p={2} textAlign="center">
          <Typography color="textSecondary">
            Salve a integração primeiro para gerar o token e as URLs.
          </Typography>
        </Box>
      );
    }
    return (
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          🔑 Token desta integração
        </Typography>
        <Box className={classes.urlBox} mb={2}>
          <span style={{ flex: 1 }}>
            {showToken ? integration.token : "•".repeat(integration.token.length)}
          </span>
          <Tooltip title={showToken ? "Esconder" : "Mostrar"}>
            <IconButton
              size="small"
              onClick={() => setShowToken((s) => !s)}
              style={{ color: "#10b981" }}
            >
              {showToken ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Copiar token">
            <IconButton
              size="small"
              onClick={() => copy(integration.token, "Token")}
              style={{ color: "#10b981" }}
            >
              <FileCopyOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Typography variant="subtitle2" gutterBottom>
          🌐 URLs para usar nos sistemas externos
        </Typography>
        <Typography className={classes.helper}>
          Copie e cole no n8n, Make, navegador ou qualquer ferramenta que faça
          requisições HTTP GET. O token já está embutido na URL.
        </Typography>

        {allUrls.length === 0 && (
          <Typography color="textSecondary" style={{ padding: 16 }}>
            Nenhum recurso liberado ainda. Volte para a aba "Recursos" e marque ao menos um.
          </Typography>
        )}
        {allUrls.map(({ resource, url }) => (
          <Box key={resource} mb={1.5}>
            <Typography variant="caption" style={{ fontWeight: 600 }}>
              {RESOURCE_LABELS[resource] || resource}
            </Typography>
            <Box className={classes.urlBox}>
              <span style={{ flex: 1 }}>{url}</span>
              <Tooltip title="Copiar URL">
                <IconButton
                  size="small"
                  onClick={() => copy(url, "URL")}
                  style={{ color: "#10b981" }}
                >
                  <FileCopyOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        ))}

        <Divider style={{ margin: "16px 0" }} />

        <Typography variant="subtitle2" gutterBottom>
          📘 Como usar com Bearer Token (alternativa)
        </Typography>
        <Typography className={classes.helper}>
          Em produção, prefira passar o token no header Authorization. Mais seguro.
        </Typography>
        <Paper variant="outlined" style={{ padding: 12, fontSize: 12, fontFamily: "monospace", background: "#f8fafc" }}>
          GET {apiBaseUrl}/api/v1/{allowedResources[0] || "stats"}
          <br />
          Authorization: Bearer {integration.token}
        </Paper>
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={() => onClose()} maxWidth="md" fullWidth>
      <DialogTitle>
        {isEdit ? "Editar API Rest" : "Nova API Rest"}
      </DialogTitle>
      <DialogContent dividers>
        <TextField
          label="Nome da integração"
          placeholder="Ex.: API para WordPress do Cliente X"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          variant="outlined"
          size="small"
          margin="dense"
        />

        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          indicatorColor="primary"
          textColor="primary"
          style={{ marginTop: 16, marginBottom: 8, borderBottom: "1px solid #eee" }}
        >
          <Tab label="Recursos & Campos" />
          <Tab label="URLs & Token" disabled={!isEdit} />
        </Tabs>

        <Box mt={2}>{tab === 0 ? renderResourceTree() : renderUrlsTab()}</Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose()} disabled={saving}>
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={saving}
        >
          {saving ? "Salvando..." : isEdit ? "Salvar alterações" : "Criar integração"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ApiRestModal;
